<template>
  <div>
    <div v-for="msg in messages" :key="msg.id">
      <div v-if="msg.new_date" class="date m-1">
        {{ inboxHelper.formatDate(msg.created_at) }}
      </div>
      <div
        :class="['message-container', {
          'in-conversation-group': msg.conversation,
          'conversation-start': msg.conversation_start,
          'conversation-end': msg.conversation_end
        }]"
      >
        <div v-if="msg.conversation_start">
          <h5 class="text-left mb-3 font-weight-semibold">
            {{ msg.conversation.source_name + " - " + inboxHelper.getDateWithOptionalYear(msg.conversation.started_at) }}
            <span v-if="msg.conversation_continued" class="font-italic">(continued)</span>
            <IncidentPill
              v-for="incident in msg.conversation.incidents"
              :key="incident.id"
              class="ml-2"
              v-bind="incident"
              :link="!embedContext"
              :baseUrl="store.apiBaseUrl"
            />
          </h5>
        </div>
        <div v-if="msg.sender" class="sender">
          {{ msg.sender }}
        </div>
        <div
          :ref="`message-${msg.id}`"
          :data-message-id="msg.id"
          class="message-container"
          tabindex="-1"
          @click="selectMessage($event, msg)"
        >
          <div class="message" :class="inboxHelper.messageClasses(msg, showAiIcon)">
            <span v-if="inboxHelper.isSimulated(msg)" class="badge badge-info">Simulated</span>
            <span
              v-b-tooltip.hover
              class="time"
              :title="inboxHelper.tooltipTime(msg)"
            >{{ inboxHelper.messageTime(msg) }}</span>
            <span class="status-icon">
              <b-icon
                :id="`icon-${msg.id}`"
                :icon="inboxHelper.getIconForStatus(inboxHelper.statusType(msg)).icon"
                :variant="inboxHelper.getIconForStatus(inboxHelper.statusType(msg)).variant"
                :font-scale="inboxHelper.getIconForStatus(inboxHelper.statusType(msg)).scale"
              />
            </span>
            <b-popover
              :target="`icon-${msg.id}`"
              title="Text Message Details"
              triggers="click blur"
              :placement="msg.direction === 'inbound' ? 'right' : 'left'"
            >
              <template #title>
                Text Message Details
              </template>
              <strong>From:</strong> {{ inboxHelper.formatNumber(msg.from_number) }}<br>
              <strong>To:</strong> {{ inboxHelper.formatNumber(msg.to_number) }}<br>
              <strong>Status:</strong> {{ inboxHelper.messageStatus(msg) }}<br>
              <strong>Sent:</strong> {{ inboxHelper.messageDetailTime(msg) }}
              <span v-if="msg.metadata">
                <br><strong>Metadata:</strong> {{ inboxHelper.messageMetadata(msg) }}
              </span>
            </b-popover>
            <span
              :class="{'emojiOnly': isJustEmoji(msg.message_text)}"
              style="white-space: pre-wrap;"
            >{{ msg.message_text }}</span>

            <ul v-if="msg.media.length > 0" class="list-inline">
              <li
                v-for="(media, idx) in msg.media"
                :key="media.sid"
                class="cursor-pointer"
              >
                <LazyImage
                  context="inbox"
                  :store="store"
                  :url="store.getImageUrl(msg.id, idx)"
                  @click.native="$emit('openImageLightbox', msg.id, idx)"
                />
              </li>
            </ul>
            <div
              v-if="showAiIcon && msg.direction === 'inbound' && store.selectedMessage && store.selectedMessage.id === msg.id"
              class="stars-container"
              @click.stop="!store.loading.suggestResponse && $emit('suggestResponse')"
            >
              <b-icon
                class="selected-icon"
                icon="stars"
                scale="2"
                :class="{'text-muted': store.loading.suggestResponse}"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LazyImage from "./LazyImage.vue";
import {isJustEmoji} from "@/Inbox/helpers/emojiHelper";
import IncidentPill from "@ui/IncidentPill.vue";

export default {
  name: "MessageView",
  components: {
    IncidentPill,
    LazyImage
  },
  props: {
    store: {
      type: Object,
      required: true
    },
    sortedMessages: {
      type: [Object, Array],
      required: true,
    },
    inboxHelper: {
      type: Object,
      required: true,
    },
    showAiIcon: {
      type: Boolean,
      required: false
    },
    embedContext: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isJustEmoji,
      selectedMessageContainer: null
    };
  },
  computed: {
    messages() {
      if (!this.sortedMessages.length) {
        return {};
      }
      const conversationList = [];
      return this.sortedMessages.map((message, index) => {
        const messageDate = message.created_at.split(" ")[0];
        const conversationId = message.study_user_conversation_id;
        const previousMessage = this.sortedMessages[index - 1];
        const nextMessage = this.sortedMessages[index + 1];

        const conversation = conversationId ? this.store.conversationsById[conversationId] || null : null
        const conversationStart = conversation ? (!previousMessage || conversationId !== previousMessage.study_user_conversation_id) : false;
        const conversationEnd = conversation ? (!nextMessage || conversationId !== nextMessage.study_user_conversation_id) : false;

        let conversationContinued = false;
        if (conversationStart) {
          conversationContinued = conversationList.indexOf(conversationId) >= 0;
          conversationList.push(conversationId);
        }

        return {
          ...message,
          new_date: !previousMessage || (messageDate !== previousMessage.created_at.split(" ")[0]),
          conversation: conversation,
          conversation_start: conversationStart,
          conversation_end: conversationEnd,
          conversation_continued: conversationContinued
        };
      });
    },
  },
  methods: {
    selectMessage: function(event, msg) {
      if (this.showAiIcon && msg.direction === 'inbound') {
        const previouslySelectedMessage = this.selectedMessageContainer;
        if (previouslySelectedMessage) {
          this.clearSelection();
          this.store.setSelectedMessage(null);
        }

        const messageContainer = event.currentTarget.parentElement;
        if (previouslySelectedMessage !== messageContainer) {
          messageContainer.classList.add('selected');
          this.selectedMessageContainer = messageContainer;
          this.store.setSelectedMessage(msg);
        }
      }
    },
    clearSelection() {
      if (this.selectedMessageContainer) {
        this.selectedMessageContainer.classList.remove('selected');
        this.selectedMessageContainer = null;
      }
    }
  }
}
</script>
<style scoped>
.date {
  clear: both;
  font-size: 1.1em;
  color: gray;
  text-align: center;
}

.badge-pill {
  font-size: 75% !important;
  font-weight: initial !important;
  padding: .25em .4em !important;
}

.message {
  position: relative;
  display: inline-block;
  min-width: 150px;
  max-width: 450px;
  padding: 10px 18px;
  clear: both;
  vertical-align: top;
  border-radius: 5px;
  -webkit-box-shadow: 0 0 6px #b2b2b2;
  box-shadow: 0 0 6px #b2b2b2;
}

/* These are all applied conditionally for conversation outlines */
.message-container.in-conversation-group {
  border-left: 2px dashed rgba(0, 0, 0, 0.125);
  border-right: 2px dashed rgba(0, 0, 0, 0.125);
  padding-left: 12px;
  padding-right: 12px;
}
.message-container.conversation-start {
  border-top: 2px dashed rgba(0, 0, 0, 0.125);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding-top: 8px;
  margin-top: 2px;
}
.message-container.conversation-end {
  border-bottom: 2px dashed rgba(0, 0, 0, 0.125);
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  padding-bottom: 8px;
  margin-bottom: 2px;
}

.emojiOnly {
  font-size: 1.8em;
}

div.sender {
  float: right;
  margin: 0 20px 0 0;
  clear: both;
  font-size: 12px;
  color: #6c6f76;
}

.inbound {
  float: left;
  margin: 5px 45px 5px 20px;
  background-color: #bdf9bd;
}

.inbound::before {
  left: -9px;
  background-color: #bdf9bd;
  -webkit-box-shadow: -2px 2px 2px 0 rgba(178, 178, 178, .4);
  box-shadow: -2px 2px 2px 0 rgba(178, 178, 178, .4);
}

.inbound.ai-suggestion-selectable:hover {
  box-shadow: 0 0 2px 2px rgba(0, 100, 0, .4);
}

.inbound.ai-suggestion-selectable:hover::before {
  box-shadow: -2px 2px 2px 0 rgba(0, 100, 0, .4);
}

.message-container.selected .inbound {
  box-shadow: 0 0 4px 4px rgba(0, 100, 0, .6);
}

.message-container.selected .inbound::before {
  box-shadow: -2px 2px 2px 0 rgba(0, 100, 0, .6);
}

.message-container.selected .outbound {
  box-shadow: 0 0 4px 4px rgba(0, 100, 0, .6);
}

.message-container.selected .outbound::before {
  box-shadow: 2px -2px 2px 0 rgba(0, 100, 0, .6);
}

.message-container.selected .selected-icon {
  color: white;
  font-size: 0.875rem;
  margin: 0;
  padding: 0;
}

.message::before {
  position: absolute;
  top: 11px;
  display: block;
  width: 20px;
  height: 16px;
  content: "\00a0";
  -webkit-transform: rotate(29deg) skew(-35deg);
  -moz-transform: rotate(29deg) skew(-35deg);
  -ms-transform: rotate(29deg) skew(-35deg);
  -o-transform: rotate(29deg) skew(-35deg);
  transform: rotate(29deg) skew(-35deg);
}

.outbound {
  float: right;
  margin: 5px 20px 5px 45px;
  background-color: #badfff;
}

.outbound::before {
  right: -9px;
  -webkit-box-shadow: 2px -2px 2px 0 rgb(178 178 178 / 40%);
  box-shadow: 2px -2px 2px 0 rgb(178 178 178 / 40%);
  background-color: #badfff;
}

.outbound.automated, .outbound.automated::before {
  background-color: #e0f1ff;
}

.outbound.manual, .outbound.manual::before {
  background-color: #badfff;
}

.message-container {
  position: relative;
  display: inline-block;
  width: 100%;
}

.message-container .inbound {
  float: left;
}

.message-container .outbound {
  float: right;
}

.message-container.selected .message .stars-container {
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 25px;
  background: linear-gradient(135deg,
  #6366f1 0%,
  #8b5cf6 25%,
  #d1d5db 50%,
  #6366f1 75%,
  #8b5cf6 100%
  );
  padding: 12px;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: 400% 400%;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.message-container.selected .message .stars-container:hover:not(:has(.text-muted)) {
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.message-container.selected .message .stars-container:has(.text-muted) {
  animation: gradientShift 3s ease-in-out infinite;
}

@keyframes gradientShift {
  0% {
    background-position: 0 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

/* Not interactive but gets focus if jump behavior is triggered */
.message-container:focus {
  outline: none;
}

</style>
